import {
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { logger } from "../../utils/logger";
import {
  createChart,
  DeepPartial,
  ChartOptions,
  ColorType,
  LastPriceAnimationMode,
  IChartApi,
  ISeriesApi,
  AreaStyleOptions,
  SeriesOptionsCommon,
  SingleValueData,
  UTCTimestamp,
  LineStyle,
  BarPrice,
  MouseEventParams,
  PriceScaleMode,
} from "lightweight-charts";
import moment from "moment";
import currency from "currency.js";
import { PriceChartContainer, Tooltip } from "./style";
import { TEXT_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { SettingsContext } from "../../contexts/SettingsContext";
import { IPriceChartProps } from "./types";
import { upColor, downColor, buildChartData } from "./helpers";

function PriceChart({
  overrideChartColor,
  timestampPriceData,
  chartStyle,
  hideAxes,
  width,
  height,
  showTimeOnly,
  hoverCallback,
  syntheticChangePercent,
}: PropsWithChildren<IPriceChartProps>) {
  const priceChartRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Subscribe so the chart re-draws with fresh --color-up/down when the user
  // changes the direction scheme.
  const { directionScheme, customDirection } = useContext(SettingsContext);

  // The chart and its series are imperative lightweight-charts handles, not
  // render data — note the discarded value slots on the `useState` pair these
  // replace: nothing ever read them. They were kept in state purely so the
  // setter's updater could read the current handle without a dependency, which
  // meant `createChart` (a DOM mutation) ran during the RENDER phase. Under
  // StrictMode that builds two charts and leaks one. Refs are the right home,
  // and let every mutation happen in the effect body.
  const prevSeriesRef = useRef<ISeriesApi<"Area"> | undefined>(undefined);
  const prevChartRef = useRef<IChartApi | undefined>(undefined);

  const changePercent = useMemo(() => {
    if (timestampPriceData.length) {
      const latestPrice = timestampPriceData[timestampPriceData.length - 1][1];
      const oldestPrice = timestampPriceData[0][1];
      const diff = Number(latestPrice) - Number(oldestPrice);
      const percentage = (diff / (Number(oldestPrice) || 1)) * 100;
      // If data is flat, fall back to syntheticChangePercent for correct color
      if (percentage === 0 && syntheticChangePercent !== undefined) {
        return syntheticChangePercent * 100;
      }
      return percentage;
    }
    return syntheticChangePercent !== undefined ? syntheticChangePercent * 100 : 0;
  }, [timestampPriceData, syntheticChangePercent]);

  const chartOptions: DeepPartial<ChartOptions> = useMemo(
    () => ({
      width,
      height,
      layout: {
        textColor: TEXT_COLORS.two,
        background: {
          type: ColorType.Solid,
          color: "transparent",
        },
        fontFamily: "BaseFont",
        attributionLogo: false,
      },
      watermark: {
        visible: false,
      },
      handleScale: false,
      handleScroll: false,
      grid: {
        horzLines: {
          visible: false,
        },
        vertLines: {
          visible: false,
        },
      },
      timeScale: {
        borderVisible: false,
        visible: !hideAxes && chartStyle === "default",
        shiftVisibleRangeOnNewBar: true,
        tickMarkFormatter: (time: UTCTimestamp) => {
          if (showTimeOnly) {
            return moment.unix(time).format("HH:mm:ss");
          }
          return moment.unix(time).format("DD MMM");
        },
      },
      rightPriceScale: {
        borderVisible: false,
        visible: !hideAxes && chartStyle === "default",
        scaleMargins: {
          bottom: 0.15,
          top: 0.05,
        },
        autoScale: true,
        mode: PriceScaleMode.Normal,
      },
      crosshair: {
        horzLine: {
          visible: false,
          labelVisible: false,
        },
        vertLine: {
          visible: chartStyle === "default",
          style: LineStyle.Solid,
          labelVisible: false,
        },
      },
    }),
    [width, height, hideAxes, chartStyle, showTimeOnly]
  );

  const color = useMemo(
    () => ({
      primary:
        overrideChartColor?.primary ||
        (changePercent >= 0 ? upColor(1) : downColor(1)),
      secondary:
        overrideChartColor?.secondary ||
        (changePercent >= 0 ? upColor(0.12) : downColor(0.12)),
      tertiary:
        overrideChartColor?.tertiary ||
        (changePercent >= 0 ? upColor(0.24) : downColor(0.24)),
    }),
    // directionScheme/customDirection: re-resolve colors when the scheme changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [changePercent, overrideChartColor, directionScheme, customDirection]
  );

  const renderTooltipCallback = useCallback(
    (param: MouseEventParams) => {
      if (!tooltipRef.current) {
        return;
      }

      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.y < 0
      ) {
        tooltipRef.current.style.display = "none";
      } else {
        tooltipRef.current.style.display = "block";

        const display = showTimeOnly
          ? `${moment(Number(param.time || 1) * 1000).format("hh:mm a")}`
          : `${moment(Number(param.time || 1) * 1000).format(
              "hh:mm a, MMM Do"
            )}`;

        tooltipRef.current.innerText = display;
        tooltipRef.current.style.bottom = `${SPACING.four}px`;

        const labelHalfWidth = showTimeOnly ? SPACING.five : SPACING.seven;
        let left = param.point.x - labelHalfWidth;
        left = Math.max(left, 0);
        tooltipRef.current.style.left = `${left}px`;
      }
    },
    [showTimeOnly]
  );

  const areaSeriesOptions: DeepPartial<AreaStyleOptions & SeriesOptionsCommon> =
    useMemo(
      () => ({
        // Gradient
        topColor: color.secondary,
        bottomColor: "transparent",
        lineColor: color.primary,
        crosshairMarkerVisible: chartStyle === "default",
        crosshairMarkerRadius: 5,
        crosshairMarkerBorderColor: color.tertiary,
        crosshairMarkerBackgroundColor: color.primary,
        lineWidth: 1,
        lastPriceAnimation: LastPriceAnimationMode.Disabled,
        priceLineVisible: false,
        baseLineVisible: false,
        lastValueVisible: false,
        priceFormat: {
          type: "custom",
          formatter: (priceValue: BarPrice) =>
            currency(priceValue, { precision: 0, symbol: "" }).format(),
        },
      }),
      [chartStyle, color]
    );

  useEffect(() => {
    // Process and deduplicate chart data
    let chartData: SingleValueData[] = buildChartData(
      timestampPriceData,
      syntheticChangePercent
    );

    const prevChart = prevChartRef.current;

    // If no chart yet, create one.
    if (!prevChart && priceChartRef.current) {
      const newChart = createChart(priceChartRef.current, chartOptions);

      // Update tooltip
      if (chartStyle === "default") {
        newChart.subscribeCrosshairMove(renderTooltipCallback);
      }

      if (hoverCallback) {
        newChart.subscribeCrosshairMove(hoverCallback);
      }
      logger.debug("Chart Data is : ", chartData);
      // Adds a series to chart, and then updates
      const areaSeries = newChart.addAreaSeries(areaSeriesOptions);
      areaSeries.setData(chartData);
      newChart.timeScale().fitContent();

      prevSeriesRef.current = areaSeries;
      prevChartRef.current = newChart;
      return;
    }

    // Chart already available, so update the series (assume it exists too).
    const prevSeries = prevSeriesRef.current;
    prevSeries?.applyOptions(areaSeriesOptions);
    prevSeries?.setData(chartData);
    prevChart?.timeScale().fitContent();
    prevChart?.applyOptions(chartOptions);
  }, [
    timestampPriceData,
    chartOptions,
    areaSeriesOptions,
    hoverCallback,
    renderTooltipCallback,
    chartStyle,
    syntheticChangePercent,
  ]);

  // Responsive chart size
  useEffect(() => {
    prevChartRef.current?.applyOptions({
      width,
      height,
    });
  }, [width, height]);

  return (
    <PriceChartContainer $width={`${width}px`} $height={`${height}px`}>
      <div style={{ width: "100%" }} ref={priceChartRef} />
      <Tooltip ref={tooltipRef} />
    </PriceChartContainer>
  );
}

export default PriceChart;
